import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AccessComponentEnum, AccessLevelEnum, Team } from 'graphql-common';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import PageOutOfRangeEmptyState from '@lib/components/PageOutOfRangeEmptyState/PageOutOfRangeEmptyState';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import useSearchParam from '@lib/hooks/useSearchParam';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';
import getDepartmentAccessGroupsTableColumns from '../utils/getDepartmentAccessGroupsTableColumns';
import { UseDepartmentQueriesResult } from '../utils/useDepartmentQueries';

type Props = {
  useDepartmentQueriesResult: UseDepartmentQueriesResult;
};

export default function DepartmentViewAccessGroups({
  useDepartmentQueriesResult,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [searchQuery] = useSearchParam();

  const {
    useDepartmentQueryHookResult,
    useDepartmentAccessGroupsQueryHookResult,
  } = useDepartmentQueriesResult;
  const { data } = useDepartmentQueryHookResult;
  const { name } = data || {};

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.DepartmentsManagement) ===
    AccessLevelEnum.Read;

  const { collection, loading, firstLoading, error, totalCount } =
    useDepartmentAccessGroupsQueryHookResult;

  const onEditDepartment = () =>
    navigate(
      APP_URLS.dashboard.departments.edit.getDynamicPath({
        pathParts: { id },
      }),
    );

  const onView = (team: Team) => {
    if (team?.id) {
      navigate(
        APP_URLS.dashboard.teams.view.getDynamicPath({
          pathParts: { id: team.id },
        }),
      );
    }
  };

  const tableColumns = getDepartmentAccessGroupsTableColumns({ onView, t });

  const isEmptyStateVisible = collection.length === 0 && !error && !loading;
  const isEmptyStateByOutOfRangePage = isEmptyStateVisible && !!totalCount;
  let emptyState: React.ReactNode = null;
  if (isEmptyStateByOutOfRangePage) {
    emptyState = <PageOutOfRangeEmptyState totalEntries={totalCount} />;
  } else if (isEmptyStateVisible) {
    emptyState = (
      <ListEmptyState
        title={t('no-department-access-groups-linked-yet')}
        text={t('no-department-access-groups-linked-text')}
        actions={
          isViewOnly
            ? []
            : [
                <Button
                  key="edit"
                  buttonText={t('edit-department')}
                  buttonType={ButtonTypes.primaryFilled}
                  leftIcon="edit"
                  onClick={onEditDepartment}
                  disabled={loading}
                />,
              ]
        }
      />
    );
  }

  const isErrorStateVisible = !!error && !loading;
  const errorState = isErrorStateVisible ? (
    <ErrorPage
      status={getStatusFromError(error)}
      description={getDescriptionFromError(error)}
    />
  ) : null;

  const isContentVisible = !error && !firstLoading && collection?.length;
  const content = isContentVisible ? (
    <>
      {(!!collection?.length || searchQuery) && (
        <Search
          placeholder={t('department-access-groups-search-placeholder')}
        />
      )}
      <Table
        columns={tableColumns}
        data={collection}
        emptyState={emptyState}
        id="departmentAccessGroups"
        loading={loading}
        totalEntries={totalCount}
      />
    </>
  ) : null;

  return (
    <WhiteBox>
      <DashboardTitleHeader
        title={name}
        actions={
          isViewOnly ? (
            <ViewOnlyChip key="view" />
          ) : (
            <Button
              key="edit"
              buttonText={t('edit')}
              buttonType={ButtonTypes.primaryOutlined}
              leftIcon="edit"
              onClick={onEditDepartment}
            />
          )
        }
      />
      {firstLoading && <CircleLoader />}
      {errorState}
      {emptyState}
      {content}
    </WhiteBox>
  );
}
